import { Award, Check, Shield, Users } from "lucide-react";
import DonationCampaignInfoCard from "./DonationCampaignInfoCard";
import DonationCampaignBeforeAfterAccordion from "./DonationCampaignBeforeAfterAccordion";
import type { DonationCampaign } from "../../donation/types";
import { formatInr } from "../../donation/utils";

type Step = 1 | 2 | 3;

interface DonationDetailSidebarProps {
  campaign: DonationCampaign;
  amount: number;
  step: Step;
  upiId: string;
  copiedUpi: boolean;
  onCopyUpi: () => void;
}

export default function DonationDetailSidebar({
  campaign,
  amount,
  step,
}: DonationDetailSidebarProps) {
  return (
    <div className="space-y-5 lg:sticky lg:top-[9.5rem]">
      <SidebarCard title="Donation summary">
        <p className="text-base font-semibold leading-snug text-[#7A1E1E]">
          {campaign.title}
        </p>
        {campaign.subTitle && (
          <p className="mt-1 text-sm text-gray-600">{campaign.subTitle}</p>
        )}
        <p className="mt-4 text-4xl font-bold tracking-tight text-[#7A1E1E]">
          {formatInr(amount)}
        </p>
        {campaign.amountType === "fixed" && campaign.fixedAmount !== null && (
          <p className="mt-1 text-xs text-gray-500">
            Fixed contribution amount
          </p>
        )}
        {campaign.amountType === "variable" &&
          (campaign.minContribution !== null ||
            campaign.maxContribution !== null) && (
            <p className="mt-1 text-xs text-gray-500">
              {campaign.minContribution !== null &&
                `Min ${formatInr(campaign.minContribution ?? 0)}`}
              {campaign.minContribution !== null &&
                campaign.maxContribution !== null &&
                " · "}
              {campaign.maxContribution !== null &&
                `Max ${formatInr(campaign.maxContribution ?? 0)}`}
            </p>
          )}

        <ul className="mt-6 space-y-3 border-t border-gray-100 pt-5">
          <SummaryItem done={step >= 1} label="Campaign & amount" />
          <SummaryItem done={step >= 2} label="Your details" />
          <SummaryItem done={step >= 3} label="Payment" />
        </ul>
      </SidebarCard>

      <DonationCampaignInfoCard campaign={campaign} />

      <DonationCampaignBeforeAfterAccordion campaign={campaign} />

      <SidebarCard title="Why donate with us">
        <Benefit icon={Award} text="80G tax exemption receipt" />
        <Benefit icon={Shield} text="100% funds reach institutions" />
        <Benefit icon={Users} text="Trusted by donors nationwide" />
      </SidebarCard>

      {/* {campaign.isCashDonation !== false && (
        <div className="rounded-2xl border border-gray-100/80 bg-white p-6 shadow-[0_4px_24px_rgba(122,30,30,0.06)]">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#7A1E1E]">
            <Smartphone className="h-4 w-4 text-[#D4A017]" />
            Pay via UPI
          </div>
          <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
            <code className="text-sm text-gray-800">{upiId}</code>
            <button
              type="button"
              onClick={onCopyUpi}
              className="rounded-lg p-2 text-[#7A1E1E] hover:bg-[#FFF5E6]"
              aria-label="Copy UPI ID"
            >
              {copiedUpi ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </button>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Use any UPI app — GPay, PhonePe, Paytm, BHIM.
          </p>
        </div>
      )} */}
    </div>
  );
}

function SidebarCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-gray-100/80 bg-white p-6 shadow-[0_4px_24px_rgba(122,30,30,0.06)]">
      <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
        {title}
      </p>
      {children}
    </div>
  );
}

function SummaryItem({ done, label }: { done: boolean; label: string }) {
  return (
    <li className="flex items-center gap-3">
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
          done ? "bg-[#D4A017] text-white" : "border-2 border-gray-200 bg-white"
        }`}
      >
        {done ? (
          <Check className="h-3.5 w-3.5 stroke-[2.5]" />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
        )}
      </span>
      <span
        className={`text-sm font-medium ${
          done ? "text-gray-800" : "text-gray-400"
        }`}
      >
        {label}
      </span>
    </li>
  );
}

function Benefit({
  icon: Icon,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
}) {
  return (
    <div className="mb-2 flex items-start gap-2 text-sm text-gray-600 last:mb-0">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-[#D4A017]" />
      {text}
    </div>
  );
}
